/**
 * BEAM CONTENT MODE — the floating close in the content stage.
 *
 * Beam (the `unbound` view) has no chrome: when the agent opens a video,
 * a PDF, or a tour, the content is a stage beside her and the only
 * control is the floating close (styles.ts "BEAM CONTENT MODE"). That
 * button pre-dates Beam — on landscape touch media it tears the whole
 * widget down, call included. In Beam the visitor is dismissing the
 * CONTENT, not the conversation: the stage closes, she stays, the call
 * goes on. Pure so the split is unit-tested.
 */

import type { AgentView, TransitionAction } from './state';
import type { PlaylistItem } from './components/playlist-drawer';

/** What the floating close does for this view. */
export function contentDismissAction(view: AgentView): TransitionAction {
  return view === 'unbound' ? { type: 'endContent' } : { type: 'close' };
}

/** Whether the floating close also hangs up a live call. */
export function stopsVoiceOnContentDismiss(view: AgentView): boolean {
  return view !== 'unbound';
}

/**
 * Cinema content — a video (file or provider embed) or a tour is a 16:9
 * picture: the Beam stage sizes itself to it (styles.ts "cinema") instead
 * of stretching a letterboxed frame to the full page height. Pages
 * (PDF / image / article) keep the tall stage — they need the height.
 */
export function isCinemaContent(it: Pick<PlaylistItem, 'content_type' | 'content_extension' | 'embed_url'> | null): boolean {
  if (!it) return false;
  if (it.content_type === 'tour' || it.content_extension === 'tour') return true;
  if (it.content_type === 'video' || it.content_extension === 'video') return true;
  return !!it.embed_url;
}

/**
 * Where an interactive card (meeting slots, contact form) mounts. Every
 * other view has the chat thread on screen; Beam has only her card, so
 * the card's SHEET hosts it (stage.tsx). Null = use the thread.
 */
export function beamInteractiveHost(view: AgentView, sheet: HTMLElement | null): HTMLElement | null {
  return view === 'unbound' && sheet ? sheet : null;
}
